import { useState } from "react";
import { PageHeader, useResource, friendlyTime } from "@/admin/shared";
import { Button, Field, Input, TextArea, Modal, StatusPill } from "@/admin/ui";
import { api, formatApiError } from "@/admin/api";

export default function Agents() {
    const { data, loading, refresh } = useResource("/agents");
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ prompt: "", limit_daily: 500 });
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    function openConfigure(a) {
        setEditing(a);
        setForm({ prompt: a.prompt || "", limit_daily: a.limit_daily || 500 });
        setErr("");
    }
    async function toggle(a) {
        try {
            await api.patch(`/agents/${a.id}`, { enabled: !a.enabled });
            await refresh();
        } catch (e) {
            alert(formatApiError(e));
        }
    }
    async function save(e) {
        e?.preventDefault?.();
        if (!editing) return;
        setBusy(true);
        setErr("");
        try {
            await api.patch(`/agents/${editing.id}`, {
                prompt: form.prompt,
                limit_daily: Number(form.limit_daily) || 0,
            });
            setEditing(null);
            await refresh();
        } catch (e2) {
            setErr(formatApiError(e2));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div data-testid="admin-agents-page">
            <PageHeader
                title="AI Agents"
                subtitle="Enable, tune, and inspect the 10 domain agents that turn team data into decisions."
            />

            {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} className="glass rounded-3xl p-6 h-[220px] animate-pulse" />
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {(data || []).map((a, i) => (
                        <div
                            key={a.id}
                            data-testid={`agent-${a.name.toLowerCase().replace(/\s+/g, "-")}`}
                            className="glass rounded-3xl p-5 flex flex-col hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-20px_rgba(139,92,246,0.35)] transition-all"
                        >
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] font-semibold tracking-[0.2em] uppercase text-violet-600 bg-violet-50 rounded-full px-2 py-0.5">
                                    {a.department}
                                </span>
                                <StatusPill status={a.enabled ? "active" : "inactive"} />
                            </div>

                            <div className="mt-6 font-display text-[19px] font-medium tracking-tight text-black">
                                {a.name}
                            </div>
                            <div className="mt-2 text-[12px] text-neutral-500">
                                Last analysis · {friendlyTime(a.last_analysis)}
                            </div>

                            <div className="mt-4 grid grid-cols-2 gap-2 text-[11.5px]">
                                <Stat label="Insights" v={a.insights_generated} />
                                <Stat label="Decisions" v={a.decisions_created} />
                                <Stat label="Sources" v={(a.data_sources || []).length} />
                                <Stat label="Daily limit" v={a.limit_daily} />
                            </div>

                            <div className="mt-5 flex items-center gap-1.5">
                                <Button size="sm" variant="ghost" onClick={() => openConfigure(a)} data-testid={`agent-config-${a.id}`}>
                                    Configure
                                </Button>
                                <Button
                                    size="sm"
                                    variant={a.enabled ? "danger" : "primary"}
                                    onClick={() => toggle(a)}
                                    data-testid={`agent-toggle-${a.id}`}
                                >
                                    {a.enabled ? "Disable" : "Enable"}
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Modal
                open={!!editing}
                onClose={() => setEditing(null)}
                title={editing ? `Configure ${editing.name}` : ""}
                size="lg"
                testid="agent-modal"
                footer={
                    <>
                        <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                        <Button onClick={save} disabled={busy} data-testid="agent-save-btn">
                            {busy ? "Saving…" : "Save"}
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <Field label="Agent Prompt" hint="System prompt used for analysis calls.">
                        <TextArea rows={5} value={form.prompt} onChange={(e) => setForm({ ...form, prompt: e.target.value })} />
                    </Field>
                    <Field label="Daily call limit">
                        <Input type="number" min="0" value={form.limit_daily} onChange={(e) => setForm({ ...form, limit_daily: e.target.value })} />
                    </Field>
                    {err && (
                        <div className="rounded-xl border border-rose-200 bg-rose-50/70 text-rose-700 text-[13px] px-3.5 py-2.5">
                            {err}
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
}

function Stat({ label, v }) {
    return (
        <div className="rounded-xl bg-white border border-black/5 px-3 py-2">
            <div className="text-[10px] tracking-[0.18em] uppercase text-neutral-500">{label}</div>
            <div className="text-[14px] font-semibold text-black">{v}</div>
        </div>
    );
}
